/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Collapse, FormGroup } from 'react-bootstrap'
import moment from 'moment'
import DatePicker from '@mdsol/lego/lib/DatePicker'
import '@mdsol/lego/lib/DatePicker.css'
import cloneDeep from 'lodash/cloneDeep'
import { Formik, type FormikProps } from 'formik'
import { useServiceContext } from '~/root'
import { WorkflowViewType, type WorkflowsFilterValues, type WorkflowsFilterOptions, type SelectOption } from './types'
import { getDefaultFilter } from './utils'
import Loader from '~/components/shared/Loader'
import { Select } from '~/components/Select'
import FsiPickerModal from '~/components/ui/FsiPickerModal'
import { InfiniteScrollSelect } from '~/components/InfiniteScrollSelect'
import { useRcmTableData } from '~/hooks/useRcmTableData'
import { WorkflowType, WorkflowStepType } from '~/constants/workflow'
import { MasterDataType } from '~/constants/etmf'
import { transformToSelectOptions } from '~/utils/selectOptions'
import { getMomentDateFormat, getDatePickerParseFormats } from '~/utils/dateFormatting'

/** QC Phase constants (migrated from lego/page/browser/qc-review/QCConstants) */
const QC_PHASE = {
  REVIEW: 'QC_REVIEW',
  APPROVAL: 'QC_APPROVAL'
} as const

const QC_WF_PHASES = [QC_PHASE.REVIEW, QC_PHASE.APPROVAL]

/** EOS constants */
const EOS_WF_PHASES = ['DOWNLOAD', 'ACKNOWLEDGE']
const EOS_WF_TYPES: readonly string[] = [WorkflowType.EOS_DOWNLOAD_AND_ACKNOWLEDGE]

/** ETMF cascade level constants (mirrors legacy context.constant.reports.level) */
const LEVEL = MasterDataType

interface WorkflowsFilterProps {
  viewType: WorkflowViewType
}

const WorkflowsFilter = (props: WorkflowsFilterProps) => {
  const { t } = useTranslation()
  const context = useServiceContext()
  const { service, api, constant: Constants } = context
  const tableData = useRcmTableData()
  const todayDate = moment()
  const persistenceKey = tableData.persistenceKey + '_filter'

  const isEosEnabled = service.init?.getFeaturesInfo?.()?.eosFeature?.show ?? false
  const isEtmfEnabled = service.init?.getFeaturesInfo?.()?.etmfFeature?.show ?? false
  const qcReviewEnabled = service.init?.getFeaturesInfo?.()?.qcReview?.qcReviewEnabled ?? false

  const defaultFilter: WorkflowsFilterValues = getDefaultFilter(isEosEnabled, isEtmfEnabled)

  const notProcessedWfPhases = useRef<string[]>([])
  const [filter, setFilter] = useState<WorkflowsFilterValues | null>(null)
  const [options, setOptions] = useState<WorkflowsFilterOptions>({
    availableUsers: [],
    availableStudies: [],
    availableSites: [],
    availableWorkflowTypes: [],
    availableWorkflowPhases: [],
    product: [],
    study: [],
    environment: [],
    country: [],
    site: []
  })
  const [activeApiCallCounter, setActiveApiCallCounter] = useState(0)
  const [isLoadingStudies, setIsLoadingStudies] = useState(false)
  const [isLoadingSites, setIsLoadingSites] = useState(false)
  const [isFsiPickerOpen, setIsFsiPickerOpen] = useState(false)

  // --- Initialization ---

  useEffect(() => {
    setActiveApiCallCounter((prev) => prev + 3)

    const persistedFilter = service.persist.getRouteScoped<WorkflowsFilterValues>(persistenceKey)
    const newFilter: WorkflowsFilterValues = persistedFilter ? { ...persistedFilter } : { ...defaultFilter }

    Promise.allSettled([
      api.workflows.getWorkflowTypes().then((resp) => {
        setOptions((opts) => ({
          ...opts,
          availableWorkflowTypes: Object.keys(resp).map((key) => ({
            value: key,
            label: (resp as Record<string, string>)[key]
          }))
        }))
      }),
      api.workflows.getWorkflowPhases().then((resp) => {
        notProcessedWfPhases.current = [...resp]
        if (qcReviewEnabled) {
          notProcessedWfPhases.current.push(QC_PHASE.REVIEW, QC_PHASE.APPROVAL)
        }
        setAndReturnProcessWfPhases(newFilter.workflowType)
      }),
      api.users.getAssembledUsersGroups().then((resp) => {
        setOptions((opts) => ({
          ...opts,
          availableUsers: transformToSelectOptions(resp.users, 'id', 'name', 'name')
        }))
      })
    ]).then(() => {
      setActiveApiCallCounter((prev) => prev - 3)
    })

    if (newFilter.startFrom) {
      newFilter.startFrom = moment(newFilter.startFrom).utc()
    }
    if (newFilter.startTo) {
      newFilter.startTo = moment(newFilter.startTo).utc()
    }

    newFilter.isEosEnabled = isEosEnabled
    newFilter.isEtmfEnabled = isEtmfEnabled

    if (newFilter.isEosEnabled && newFilter.isEosSelected) {
      loadStudies()
      if (newFilter.study) {
        loadSites(newFilter.study)
      }
    }

    setFilter({
      ...newFilter,
      data: {
        product: newFilter.data?.product ?? null,
        study: newFilter.data?.study ?? null,
        environment: newFilter.data?.environment ?? null,
        country: newFilter.data?.country ?? null,
        site: newFilter.data?.site ?? null
      }
    })

    onProductChange(newFilter.data?.product, LEVEL.SITE, newFilter).then((dataOpts) => {
      doFilter(newFilter, null, dataOpts)
    })
  }, [])

  useEffect(() => {
    if (filter?.isEtmfEnabled) {
      loadData()
    }
  }, [filter?.isEtmfEnabled])

  // --- Core filter logic ---

  const doFilter = (
    values: WorkflowsFilterValues,
    _formikBag?: FormikProps<{ filter: WorkflowsFilterValues }> | null,
    _existingOpts?: Partial<WorkflowsFilterOptions>
  ) => {
    const valuesToBePassed = cloneDeep(values)

    // Auto-add QC_REVIEW workflow type when only QC phases are selected
    if (valuesToBePassed?.workflowType?.length === 0) {
      const hasQcPhases =
        (valuesToBePassed?.workflowPhase?.length === 2 &&
          valuesToBePassed.workflowPhase.includes(QC_PHASE.REVIEW) &&
          valuesToBePassed.workflowPhase.includes(QC_PHASE.APPROVAL)) ||
        (valuesToBePassed?.workflowPhase?.length === 1 &&
          (valuesToBePassed.workflowPhase.includes(QC_PHASE.REVIEW) ||
            valuesToBePassed.workflowPhase.includes(QC_PHASE.APPROVAL)))
      if (hasQcPhases) {
        valuesToBePassed.workflowType.push(WorkflowType.QC_REVIEW)
      }
    }

    // Transform QC phases to standard step types for the API
    const wfPhasesClone = cloneDeep(valuesToBePassed?.workflowPhase)
    const WfPhases =
      qcReviewEnabled && Array.isArray(wfPhasesClone) && wfPhasesClone.length
        ? Array.from(
            new Set(
              wfPhasesClone.map((phase) =>
                phase === QC_PHASE.REVIEW
                  ? WorkflowStepType.REVIEW
                  : phase === QC_PHASE.APPROVAL
                    ? WorkflowStepType.APPROVE
                    : phase
              )
            )
          )
        : wfPhasesClone

    tableData.applyFilter({ ...valuesToBePassed, workflowPhase: WfPhases })
    service.persist.setRouteScoped(persistenceKey, { ...valuesToBePassed, workflowPhase: WfPhases })
  }

  const setAndReturnProcessWfPhases = (selectedTypes: string[]): string[] => {
    const availablePhases =
      selectedTypes?.length > 0
        ? selectedTypes.includes(WorkflowType.QC_REVIEW)
          ? notProcessedWfPhases.current
          : notProcessedWfPhases.current.filter((item) => item !== QC_PHASE.REVIEW && item !== QC_PHASE.APPROVAL)
        : notProcessedWfPhases.current

    if (availablePhases && availablePhases.length) {
      let hasEos = false
      let hasStandard = false

      selectedTypes.forEach((type) => {
        if (EOS_WF_TYPES.includes(type)) {
          hasEos = true
        } else {
          hasStandard = true
        }
      })

      const wfPhases: string[] =
        (!hasEos && !hasStandard) || (hasEos && hasStandard)
          ? isEosEnabled
            ? [...availablePhases]
            : availablePhases.filter((p) => !EOS_WF_PHASES.includes(p))
          : hasEos
            ? [...EOS_WF_PHASES]
            : availablePhases.filter((p) => !EOS_WF_PHASES.includes(p))

      if (qcReviewEnabled && selectedTypes.includes(WorkflowType.QC_REVIEW)) {
        if (selectedTypes.length === 1) {
          wfPhases.length = 0
        }
        QC_WF_PHASES.forEach((item) => {
          if (!wfPhases.includes(item)) {
            wfPhases.push(item)
          }
        })
      }

      setOptions((opts) => ({
        ...opts,
        availableWorkflowPhases: wfPhases.map((i) => ({
          value: i,
          label: t('WF_PHASE_' + i)
        }))
      }))

      return wfPhases
    }

    return []
  }

  // --- EOS data loaders ---

  const loadStudies = () => {
    setIsLoadingStudies(true)
    api.eos
      .getAssignedStudies()
      .then((resp) => {
        setOptions((opts) => ({
          ...opts,
          availableStudies: transformToSelectOptions(resp, 'studyFolderId', 'studyName', 'studyName')
        }))
      })
      .finally(() => {
        setIsLoadingStudies(false)
      })
  }

  const loadSites = (studyFolderId: number) => {
    setIsLoadingSites(true)
    api.eos
      .getSitesUnderStudy(studyFolderId, [])
      .then((resp) => {
        setOptions((opts) => ({
          ...opts,
          availableSites: transformToSelectOptions(resp, 'siteFolderId', 'siteName', 'siteName')
        }))
      })
      .finally(() => {
        setIsLoadingSites(false)
      })
  }

  // --- ETMF data loaders ---

  const loadData = async () => {
    return await api.etmf.getSelectionsForEtmfReports(LEVEL.PRODUCT, undefined, false).then((data) => {
      setOptions((optPrev) => ({
        ...optPrev,
        product: transformToSelectOptions(data, 'fsiId', 'fsiName', null, true)
      }))
    })
  }

  // --- ETMF cascade handlers ---

  function onProductChange(
    product: SelectOption | null,
    level: string,
    values: WorkflowsFilterValues,
    setFieldValue?: FormikProps<{ filter: WorkflowsFilterValues }>['setFieldValue']
  ): Promise<Partial<WorkflowsFilterOptions> | undefined> {
    return new Promise((resolve) => {
      if (product) {
        const fsiId = (product as unknown as Record<string, unknown>).fsiId as number | string
        api.etmf.getSelectionsForEtmfReports(LEVEL.STUDY, fsiId, false).then((data) => {
          let selectedStudy = values.data?.study ?? null
          const studyOpt = transformToSelectOptions(data, 'fsiId', 'fsiName', null, true)
          setOptions((prev) => ({ ...prev, study: studyOpt }))

          if (data?.length === 1) {
            selectedStudy = isMultiStudy(level) ? [studyOpt[0]] : [studyOpt[0]]
          } else if (Array.isArray(values.data?.study)) {
            const studyFsiIds = studyOpt.map((s: Record<string, unknown>) => s.fsiId)
            const intersect = (values.data.study as Array<Record<string, unknown>>).filter(
              (s) => studyFsiIds.indexOf(s.fsiId) > -1
            )
            selectedStudy = studyOpt.filter((i: Record<string, unknown>) =>
              intersect.some((s) => s.fsiId === i.fsiId)
            ) as SelectOption[]
          }

          onStudyChange(selectedStudy as SelectOption[] | null, level, values, setFieldValue, studyOpt).then(
            (existingOptions) => resolve({ ...existingOptions, product: options?.product, study: studyOpt })
          )
        }, resolve)
      } else {
        resolve(undefined)
      }
    })
  }

  function onStudyChange(
    selectedStudy: SelectOption[] | null,
    level: string,
    values: WorkflowsFilterValues,
    setFieldValue?: FormikProps<{ filter: WorkflowsFilterValues }>['setFieldValue'],
    studyOpts?: SelectOption[]
  ): Promise<Partial<WorkflowsFilterOptions> | undefined> {
    return new Promise((resolve) => {
      if (selectedStudy?.length) {
        const fsiId = (selectedStudy[0] as unknown as Record<string, unknown>).fsiId as number | string
        api.etmf.getSelectionsForEtmfReports(LEVEL.ENVIRONMENT, fsiId, false).then((data) => {
          let selectedEnv = values?.data?.environment ?? null
          const envOpts = transformToSelectOptions(data, 'fsiId', 'fsiName', null, true)
          setOptions((prev) => ({ ...prev, environment: envOpts }))

          if (envOpts?.length === 1) {
            selectedEnv = envOpts[0]
          }

          onEnvironmentChange(selectedEnv, level, values, setFieldValue, envOpts).then((existingOptions) =>
            resolve({ ...existingOptions, environment: envOpts })
          )
        }, resolve)
      } else {
        resolve(undefined)
      }
    })
  }

  function onEnvironmentChange(
    selectedEnv: SelectOption | null,
    level: string,
    values: WorkflowsFilterValues,
    setFieldValue?: FormikProps<{ filter: WorkflowsFilterValues }>['setFieldValue'],
    _newEnvOpts?: SelectOption[]
  ): Promise<Partial<WorkflowsFilterOptions> | undefined> {
    return new Promise((resolve) => {
      const fsiId = (selectedEnv as Record<string, unknown> | null)?.fsiId as number | string | undefined
      if (fsiId) {
        api.etmf.getSelectionsForEtmfReports(LEVEL.COUNTRY, fsiId, false).then((data) => {
          let selectedCountry = values?.data?.country ?? null
          const countryOpts = transformToSelectOptions(data, 'fsiId', 'fsiName', null, true)
          setOptions((prev) => ({ ...prev, country: countryOpts }))

          if (countryOpts?.length === 1) {
            selectedCountry = countryOpts[0]
          }

          onCountryChange(selectedCountry, level, values, setFieldValue, countryOpts).then((existingOptions) =>
            resolve({ ...existingOptions, country: countryOpts })
          )
        }, resolve)
      } else {
        resolve(undefined)
      }
    })
  }

  function onCountryChange(
    selectedCountry: SelectOption | null,
    level: string,
    values: WorkflowsFilterValues,
    setFieldValue?: FormikProps<{ filter: WorkflowsFilterValues }>['setFieldValue'],
    _countryOpts?: SelectOption[]
  ): Promise<Partial<WorkflowsFilterOptions> | undefined> {
    return new Promise((resolve) => {
      const fsiId = (selectedCountry as Record<string, unknown> | null)?.fsiId as number | string | undefined
      if (fsiId) {
        api.etmf.getSelectionsForEtmfReports(LEVEL.SITE, fsiId, false).then((data) => {
          let selectedSite = values?.data?.site ?? null
          const siteOpts = transformToSelectOptions(data, 'fsiId', 'fsiName', null, true)
          setOptions((prev) => ({ ...prev, site: siteOpts }))

          if (siteOpts?.length === 1) {
            selectedSite = siteOpts[0]
          }

          onSiteChange(selectedSite, level, values, setFieldValue, siteOpts).then((existingOptions) =>
            resolve({ ...existingOptions, site: siteOpts })
          )
        }, resolve)
      } else {
        resolve(undefined)
      }
    })
  }

  function onSiteChange(
    selectedSite: SelectOption | null,
    _level: string,
    _values: WorkflowsFilterValues,
    _setFieldValue?: FormikProps<{ filter: WorkflowsFilterValues }>['setFieldValue'],
    siteOpts?: SelectOption[]
  ): Promise<Partial<WorkflowsFilterOptions> | undefined> {
    return new Promise((resolve) => {
      const fsiId = (selectedSite as Record<string, unknown> | null)?.fsiId as number | string | undefined
      if ((fsiId && siteOpts?.length) || !fsiId) {
        resolve(undefined)
      }
    })
  }

  // --- ETMF visibility helpers ---

  function showEnvironment(level: string): boolean {
    return ([LEVEL.COUNTRY, LEVEL.SITE] as string[]).indexOf(level) >= 0
  }

  function showCountry(level: string): boolean {
    return ([LEVEL.COUNTRY, LEVEL.SITE] as string[]).indexOf(level) >= 0
  }

  function showSite(level: string): boolean {
    return ([LEVEL.SITE] as string[]).indexOf(level) >= 0
  }

  function clearProps(
    skipUntilProp: string,
    setFieldValue: FormikProps<{ filter: WorkflowsFilterValues }>['setFieldValue'],
    values: { filter: WorkflowsFilterValues }
  ): { filter: WorkflowsFilterValues } | null {
    const newVals = values ? JSON.parse(JSON.stringify(values)) : null
    const orderedProps = ['product', 'study', 'environment', 'country', 'site'] as const

    orderedProps.reduce((skip: boolean, propName) => {
      if (propName === skipUntilProp) {
        skip = false
      }

      if (!skip) {
        setOptions((prev) => ({ ...prev, [propName]: [] }))
        setFieldValue(`filter.data.${propName}`, null)
        if (newVals) {
          newVals.filter.data[propName] = null
        }
      }
      return skip
    }, true)

    return newVals
  }

  function isMultiStudy(level: string): boolean {
    return !showEnvironment(level) && !showCountry(level) && !showSite(level)
  }

  // --- Render ---

  return (
    <Collapse in={!tableData.isFilterCollapsed}>
      <div className="filter-wrapper">
        <Loader blocking={activeApiCallCounter > 0}>
          {filter && (
            <Formik
              initialValues={{ filter }}
              innerRef={(el) => {
                context.store.formRef = el as FormikProps<{ filter: WorkflowsFilterValues }>
              }}
              onReset={(values) => {
                setFilter(defaultFilter)
                values.filter = defaultFilter
                setAndReturnProcessWfPhases(defaultFilter.workflowType)
              }}
              enableReinitialize={true}
              onSubmit={(values) => {
                doFilter(values.filter)
              }}
            >
              {({ values, setFieldValue, handleSubmit, handleReset, setFieldTouched }) => {
                const selectInitiator = (
                  <FormGroup controlId="40423e4c-4bae-437f-b65b-999648d05dea" className="form-group">
                    <label className="control-label fs14">{t('WORKFLOW_INITIATOR_LABEL')}</label>
                    <Select
                      placeholder={t('USER(S)...')}
                      name="filter.initiatorId"
                      onChange={(option) => {
                        setFieldValue(
                          'filter.initiatorId',
                          option ? (option as SelectOption[]).map((o) => o.value) : []
                        )
                      }}
                      value={values.filter.initiatorId}
                      options={options.availableUsers}
                      multi={true}
                      showNewOptionAtTop={true}
                      searchable
                      id="40423e4c-4bae-437f-b65b-999648d05dea"
                    />
                  </FormGroup>
                )

                const selectCurrentPhase = (
                  <FormGroup controlId="203b5203-4ec0-4515-a3db-9a6364983e33" className="form-group">
                    <label className="control-label fs14">{t('CURRENT_PHASE')}</label>
                    <Select
                      placeholder={t('WF_CURRENT_PHASE_MULTISELECT_PLACEHOLDER')}
                      name="filter.workflowPhase"
                      onChange={(option) => {
                        setFieldValue(
                          'filter.workflowPhase',
                          option ? (option as SelectOption[]).map((o) => o.value) : []
                        )
                      }}
                      value={values.filter.workflowPhase}
                      options={options.availableWorkflowPhases}
                      multi={true}
                      showNewOptionAtTop={true}
                      searchable
                      id="203b5203-4ec0-4515-a3db-9a6364983e33"
                    />
                  </FormGroup>
                )

                const selectAssignedTo = (
                  <FormGroup controlId="e06c6c1a-0b6c-484b-8c84-56b113f5a02a" className="form-group">
                    <label className="control-label fs14">{t('ASSIGNED_TO')}</label>
                    <Select
                      placeholder={t('USER(S)...')}
                      name="filter.assignedTo"
                      onChange={(option) => {
                        setFieldValue('filter.assignedTo', option ? (option as SelectOption[]).map((o) => o.value) : [])
                      }}
                      value={values.filter.assignedTo}
                      options={options.availableUsers}
                      multi={true}
                      showNewOptionAtTop={true}
                      searchable
                      id="e06c6c1a-0b6c-484b-8c84-56b113f5a02a"
                    />
                  </FormGroup>
                )

                const selectStartDate = (
                  <FormGroup controlId="3f7c5958-e690-4475-974e-0f8b3afd735d" className="form-group">
                    <label className="control-label">{t('REPORTS_FILTER_LABEL_FROM')}</label>
                    {/* @ts-expect-error - Lego DatePicker lacks TypeScript definitions */}
                    <DatePicker
                      name="filter.from"
                      onChange={(val: moment.Moment | null) => {
                        setFieldValue('filter.startFrom', val)
                      }}
                      selected={values.filter.startFrom}
                      popoverTargetOffset="3px 0"
                      todayButton="Today"
                      dateFormatCalendar="MMMM"
                      dateFormat={getDatePickerParseFormats()}
                      maxDate={values.filter.startTo || todayDate}
                      showYearDropdown
                      className="form-control field-control w100p"
                      id="3f7c5958-e690-4475-974e-0f8b3afd735d"
                    />
                  </FormGroup>
                )

                const selectEndDate = (
                  <FormGroup controlId="6babdc72-5875-4911-9b93-7a2f944d2033" className="form-group">
                    <label className="control-label">{t('REPORTS_FILTER_LABEL_TO')}</label>
                    {/* @ts-expect-error - Lego DatePicker lacks TypeScript definitions */}
                    <DatePicker
                      name="filter.startTo"
                      onChange={(val: moment.Moment | null) => {
                        setFieldValue('filter.startTo', val)
                      }}
                      selected={values.filter.startTo}
                      popoverTargetOffset="3px 0"
                      todayButton="Today"
                      dateFormatCalendar="MMMM"
                      minDate={values.filter.startFrom}
                      maxDate={todayDate}
                      dateFormat={getDatePickerParseFormats()}
                      showYearDropdown
                      className="form-control field-control w100p"
                      id="6babdc72-5875-4911-9b93-7a2f944d2033"
                    />
                  </FormGroup>
                )

                const docTypeDropdown = (
                  <FormGroup controlId="9766d96c-ef43-41b4-821e-f932f02b54bb" className="form-group">
                    <label className="control-label">{t('DOC_TYPE_TITLE_ACTIVE_WF')}</label>
                    <InfiniteScrollSelect
                      placeholder={t('DOC_TYPE_PLACEHOLDER_ACTIVE_WF')}
                      name="filter.documentTypeTemplateId"
                      value={values?.filter?.documentTypeTemplateId as unknown as undefined}
                      asyncLoadFn={async (search, page) => {
                        const response = await api.documentTypes.getOptionsForAsyncPicker(search, page)
                        return {
                          options: response.options,
                          hasMore: response.hasMore
                        }
                      }}
                      onChange={(option) => {
                        setFieldValue('filter.documentTypeTemplateId', option)
                      }}
                      multi={false}
                      showNewOptionAtTop={true}
                      searchable
                      id="c980b30c-582d-42f3-afe8-753305ef7ca9"
                    />
                  </FormGroup>
                )

                return (
                  <>
                    <div className="row">
                      <div className="col-xs-4">
                        <FormGroup controlId="96787273-c71a-424f-8c55-aac8c794e9dc" className="form-group">
                          <label className="control-label">{t('CHANGE_REQUEST_TYPE')}</label>
                          <Select
                            placeholder={t('WF_TYPES_MULTISELECT_PLACEHOLDER')}
                            name="filter.workflowType"
                            onChange={(option) => {
                              const selectedWfTypes = option ? (option as SelectOption[]).map((o) => o.value) : []
                              setFieldValue('filter.workflowType', selectedWfTypes)

                              const newPhases = setAndReturnProcessWfPhases(selectedWfTypes)
                              setFieldValue(
                                'filter.workflowPhase',
                                values.filter.workflowPhase.filter((p) => newPhases.includes(p))
                              )

                              if (values.filter.isEosEnabled) {
                                const eosSelected = selectedWfTypes.some((type) => EOS_WF_TYPES.includes(type))
                                setFieldValue('filter.isEosSelected', eosSelected)

                                if (!eosSelected) {
                                  setFieldValue('filter.study', null)
                                  setFieldValue('filter.site', null)
                                } else if (!options.availableStudies.length) {
                                  loadStudies()
                                }
                              }
                            }}
                            value={values.filter.workflowType}
                            options={options.availableWorkflowTypes}
                            multi={true}
                            searchable
                            showNewOptionAtTop={true}
                            id="96787273-c71a-424f-8c55-aac8c794e9dc"
                          />
                        </FormGroup>
                        <FormGroup controlId="860e2242-3726-414a-a01e-c24a98bb8b49" className="form-group">
                          <label className="control-label fs14">{t('WORKFLOW_CHANGE_REQUEST_NUMBER')}</label>
                          <InfiniteScrollSelect
                            asyncLoadFn={async (search, page) => {
                              const response = await api.workflows.getOptionsForAsyncPicker(search, page)
                              return {
                                options: response.options,
                                hasMore: response.hasMore
                              }
                            }}
                            placeholder={t('WF_NUMBERS_MULTISELECT_PLACEHOLDER')}
                            name="filter.id"
                            onChange={(option) => {
                              setFieldValue('filter.id', option ? option : [])
                            }}
                            value={values.filter.id}
                            multi={true}
                            searchable
                            showNewOptionAtTop={true}
                            id="860e2242-3726-414a-a01e-c24a98bb8b49"
                          />
                        </FormGroup>
                        {props.viewType !== WorkflowViewType.FINISHED &&
                          !values.filter.isEtmfEnabled &&
                          !values.filter.isEosEnabled &&
                          docTypeDropdown}
                        {props.viewType !== WorkflowViewType.FINISHED &&
                          values.filter.isEtmfEnabled &&
                          selectCurrentPhase}
                        {values.filter.isEosEnabled && selectInitiator}
                        {values.filter.isEtmfEnabled && (
                          <FormGroup controlId="9766d96c-ef43-41b4-821e-f932f02b54bc" className="form-group">
                            <label className="control-label fs14">{t('REPORTS_FILTER_LABEL_FOLDER')}</label>
                            <p
                              className={
                                'fsi-picker-input' +
                                (values.filter.folderId && (values.filter.folderId as { id: number; name: string }).name
                                  ? ''
                                  : ' empty')
                              }
                              onClick={() => {
                                setIsFsiPickerOpen(true)
                              }}
                            >
                              {values.filter.folderId && (values.filter.folderId as { id: number; name: string }).name
                                ? (values.filter.folderId as { id: number; name: string }).name
                                : t('-SELECT_FOLDER-')}
                            </p>
                            {isFsiPickerOpen && (
                              <FsiPickerModal
                                options={{
                                  selector: 'folder',
                                  showRC: true,
                                  showSystem: true,
                                  selectedId:
                                    (values?.filter?.folderId as { id: number; name: string } | null)?.id ?? null,
                                  leafIds: null
                                }}
                                title={t('SELECT_FOLDER')}
                                isOpen={isFsiPickerOpen}
                                onClose={() => {
                                  setIsFsiPickerOpen(false)
                                }}
                                onSelect={(fsi: { id: string | number; name: string }) => {
                                  setIsFsiPickerOpen(false)
                                  if (fsi && fsi.id) {
                                    setFieldValue('filter.folderId', {
                                      id: fsi.id,
                                      name: fsi.name
                                    })
                                  }
                                }}
                              />
                            )}
                          </FormGroup>
                        )}
                        {props.viewType !== WorkflowViewType.FINISHED && values.filter.isEtmfEnabled && docTypeDropdown}
                        {props.viewType !== WorkflowViewType.FINISHED && values.filter.isEosEnabled && docTypeDropdown}
                      </div>
                      <div className="col-xs-4">
                        {!values.filter.isEosEnabled && selectInitiator}
                        {props.viewType !== WorkflowViewType.FINISHED &&
                          !values.filter.isEtmfEnabled &&
                          !values.filter.isEosEnabled &&
                          selectCurrentPhase}
                        {props.viewType !== WorkflowViewType.FINISHED &&
                          values.filter.isEtmfEnabled &&
                          selectAssignedTo}
                        {values.filter.isEosEnabled && (
                          <div>
                            <FormGroup controlId="64ef11fe-e9c7-4ae1-8551-ce9604ef9679" className="form-group">
                              <label className="control-label fs14">{t('EOS_SETUP_STUDY')}</label>
                              <Select
                                placeholder={t('STUDY...')}
                                name="filter.study"
                                onChange={(option) => {
                                  const selectedStudy = option ? (option as SelectOption).value : null
                                  setFieldValue('filter.study', selectedStudy)
                                  setFieldValue('filter.site', null)
                                  if (selectedStudy) {
                                    loadSites(selectedStudy)
                                  } else {
                                    setOptions((opts) => ({
                                      ...opts,
                                      availableSites: []
                                    }))
                                  }
                                }}
                                value={values.filter.study}
                                options={options.availableStudies}
                                isDisabled={!values.filter.isEosSelected || isLoadingStudies}
                                showNewOptionAtTop={true}
                                searchable
                                id="64ef11fe-e9c7-4ae1-8551-ce9604ef9679"
                              />
                            </FormGroup>
                            <FormGroup controlId="628ab442-134e-4bbf-99c9-d9f329695df7" className="form-group">
                              <label className="control-label fs14">{t('EMTF_MD_SITE')}</label>
                              <Select
                                placeholder={t('SITE...')}
                                name="filter.site"
                                onChange={(option) => {
                                  setFieldValue('filter.site', option ? (option as SelectOption).value : null)
                                }}
                                value={values.filter.site}
                                isDisabled={!values.filter.study || isLoadingSites}
                                options={options.availableSites}
                                showNewOptionAtTop={true}
                                searchable
                                id="628ab442-134e-4bbf-99c9-d9f329695df7"
                              />
                            </FormGroup>
                          </div>
                        )}

                        {values.filter.isEtmfEnabled && (
                          <div className="form-group w80p">
                            <label className="fs14 control-label">{t('REPORTS_FILTER_LABEL_START_DATE')}</label>
                            <div className="pl10 pt4 pr71">
                              {values.filter.isEtmfEnabled && selectStartDate}
                              {values.filter.isEtmfEnabled && selectEndDate}
                            </div>
                          </div>
                        )}
                        {props.viewType !== WorkflowViewType.FINISHED &&
                          values.filter.isEosEnabled &&
                          selectCurrentPhase}
                      </div>
                      <div className="col-xs-4">
                        {props.viewType !== WorkflowViewType.FINISHED &&
                          !values.filter.isEtmfEnabled &&
                          selectAssignedTo}
                        {!values.filter.isEtmfEnabled && (
                          <div className="form-group w80p">
                            <label className="control-label fs14">{t('REPORTS_FILTER_LABEL_START_DATE')}</label>
                            <div className="pl10 pt4 pr71">
                              {!values.filter.isEtmfEnabled && selectStartDate}
                              {!values.filter.isEtmfEnabled && selectEndDate}
                            </div>
                          </div>
                        )}
                        {values.filter.isEtmfEnabled && (
                          <div>
                            <FormGroup controlId="01e4a9c6-71ba-4288-9bf9-726f0a425ee4" className="form-group">
                              <label className="control-label fs14">{t('EMTF_MD_PRODUCT')}</label>
                              <Select
                                placeholder={t('SELECT_PRODUCT')}
                                name="filter.data.product"
                                value={
                                  values.filter.data.product ? (values.filter.data.product as SelectOption).value : null
                                }
                                options={options.product}
                                onChange={(selectedProduct) => {
                                  const newVals = clearProps('study', setFieldValue, values)
                                  setFieldValue('filter.data.product', selectedProduct)
                                  onProductChange(
                                    selectedProduct as SelectOption | null,
                                    LEVEL.SITE,
                                    newVals ? newVals.filter : values.filter,
                                    setFieldValue
                                  )
                                  setTimeout(() => setFieldTouched('filter.data.product', true), 0)
                                }}
                                multi={false}
                                showNewOptionAtTop={true}
                                isDisabled={!options.product?.length}
                                isClearable={false}
                                id="a22b9c8a-a42a-4bf8-ad57-59fe61148338"
                              />
                            </FormGroup>
                            <FormGroup controlId="8a21c38c-7219-49df-9fb8-45b6af3c1244" className="form-group">
                              <label className="control-label fs14">{t('EMTF_MD_STUDY')}</label>
                              <Select
                                isMulti={true}
                                placeholder={t('SELECT_STUDY')}
                                name="filter.data.study"
                                value={values.filter.data.study}
                                options={options.study}
                                onChange={(selectedOption) => {
                                  setFieldValue(
                                    'filter.data.study',
                                    selectedOption ? (selectedOption as SelectOption[]).map((o) => o.value) : []
                                  )
                                  const newVals = clearProps('environment', setFieldValue, values)
                                  onStudyChange(
                                    selectedOption as SelectOption[] | null,
                                    LEVEL.SITE,
                                    newVals ? newVals.filter : values.filter,
                                    setFieldValue,
                                    options.study
                                  )
                                  setTimeout(() => setFieldTouched('filter.data.study', true), 0)
                                }}
                                showNewOptionAtTop={true}
                                isDisabled={!values.filter.data.product || !options.study?.length}
                                isClearable={false}
                                id="52741926-5462-4b0a-a344-59e5ecae8b26"
                              />
                            </FormGroup>

                            <FormGroup controlId="a4443a26-a630-11ed-afa1-0242ac120002" className="form-group">
                              <label className="control-label fs14">{t('EMTF_MD_ENVIRONMENT')}</label>
                              <Select
                                placeholder={t('SELECT_ENVIRONMENT')}
                                name="filter.data.environment"
                                value={
                                  values.filter.data.environment
                                    ? (values.filter.data.environment as SelectOption).value
                                    : null
                                }
                                options={options.environment}
                                onChange={(selectedEnv) => {
                                  setFieldValue('filter.data.environment', selectedEnv)
                                  const newVals = clearProps('country', setFieldValue, values)
                                  onEnvironmentChange(
                                    selectedEnv as SelectOption | null,
                                    LEVEL.SITE,
                                    newVals ? newVals.filter : values.filter,
                                    setFieldValue,
                                    options.environment
                                  )
                                  setTimeout(() => setFieldTouched('filter.data.environment', true), 0)
                                }}
                                isMulti={false}
                                showNewOptionAtTop={true}
                                isDisabled={
                                  !values.filter.data.study ||
                                  !options.environment?.length ||
                                  (Array.isArray(values.filter.data.study) && values.filter.data.study.length > 1)
                                }
                                isClearable={false}
                                id="60f015b1-0323-4cb0-ab2e-ebe3bc870cec"
                              />
                            </FormGroup>

                            <FormGroup controlId="0b5d2bfa-b924-482d-955e-584a8c426226" className="form-group">
                              <label className="control-label fs14">{t('EMTF_MD_COUNTRY')}</label>
                              <Select
                                placeholder={t('ETMF_LABEL_SELECT_COUNTRY')}
                                name="filter.data.country"
                                value={
                                  values.filter.data.country ? (values.filter.data.country as SelectOption).value : null
                                }
                                options={options.country}
                                onChange={(selectedCountry) => {
                                  setFieldValue('filter.data.country', selectedCountry)
                                  const newVals = clearProps('site', setFieldValue, values)
                                  onCountryChange(
                                    selectedCountry as SelectOption | null,
                                    LEVEL.SITE,
                                    newVals ? newVals.filter : values.filter,
                                    setFieldValue,
                                    options.country
                                  )
                                  setTimeout(() => setFieldTouched('filter.data.country', true), 0)
                                }}
                                multi={false}
                                showNewOptionAtTop={true}
                                isDisabled={!values.filter.data.environment || !options.country?.length}
                                isClearable={false}
                                id="bfe89653-41fe-40a9-96b7-321c06b63662"
                              />
                            </FormGroup>

                            <FormGroup controlId="92041ade-a630-11ed-afa1-0242ac120002" className="form-group">
                              <label className="control-label fs14">{t('REPORTS_FILTER_LABEL_SITE')}</label>
                              <Select
                                placeholder={t('SELECT_SITE')}
                                name="filter.data.site"
                                value={values.filter.data.site ? (values.filter.data.site as SelectOption).value : null}
                                options={options.site}
                                onChange={(selectedSite) => {
                                  setFieldValue('filter.data.site', selectedSite)
                                  onSiteChange(
                                    selectedSite as SelectOption | null,
                                    LEVEL.SITE,
                                    values.filter,
                                    setFieldValue,
                                    options.site
                                  )
                                  setTimeout(() => setFieldTouched('filter.data.site', true), 0)
                                }}
                                multi={false}
                                showNewOptionAtTop={true}
                                isDisabled={!values.filter.data.country || !options.site?.length}
                                isClearable={false}
                                id="517f043e-21a8-4492-bef6-1fd85f7575ab"
                              />
                            </FormGroup>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="clearfix">
                      <div className="pull-left">
                        <Button
                          data-action="filter"
                          disabled={tableData.isLoading}
                          className="btn btn-primary"
                          onClick={() => handleSubmit()}
                          id="a7728b4c-476f-4fb9-a6af-6982156e5153"
                        >
                          {t('BTN_FILTER')}
                        </Button>
                        <Button
                          data-action="reset"
                          disabled={JSON.stringify(defaultFilter) === JSON.stringify(values.filter)}
                          className="btn btn-chita btn-link at-reset"
                          variant="link"
                          onClick={() => handleReset()}
                          id="131d6033-ee66-49c7-bfb4-21d65bc84ecc"
                        >
                          {t('BTN_CLEAR_FILTER')}
                        </Button>
                      </div>
                    </div>
                  </>
                )
              }}
            </Formik>
          )}
        </Loader>
      </div>
    </Collapse>
  )
}

export default WorkflowsFilter
